import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button, Field, Input, Label, Select, Text } from '@ankor-io/ui-components'

export type DepositConnectedAccount = {
  id: string
  name: string
  vesselName: string
}

export type DepositConnectedAccountOverride = {
  connectedAccountId: string
  feeAmount: number
}

export type ConnectedAccountDepositOverridesProps = {
  accounts: DepositConnectedAccount[]
  overrides: DepositConnectedAccountOverride[]
  isSubmitting: boolean
  onAdd: (connectedAccountId: string, feeAmount: number) => Promise<void>
  onDelete: (connectedAccountId: string) => Promise<void>
  onValidationError: (message: string) => void
}

/**
 * Connected-account deposit overrides for the deposit card. Deposit charges only apply to APA
 * connected accounts, so only APA accounts are offered. Deleting an override falls back to the
 * organization amount for that account.
 */
export function ConnectedAccountDepositOverrides({
  accounts,
  overrides,
  isSubmitting,
  onAdd,
  onDelete,
  onValidationError,
}: ConnectedAccountDepositOverridesProps) {
  const [isAdding, setIsAdding] = useState(false)
  const [selectedAccountId, setSelectedAccountId] = useState('')
  const [amount, setAmount] = useState('')

  const accountName = (id: string) => accounts.find((account) => account.id === id)?.name ?? id
  const overriddenIds = new Set(overrides.map((override) => override.connectedAccountId))
  const availableAccounts = accounts.filter((account) => !overriddenIds.has(account.id))

  const resetForm = () => {
    setSelectedAccountId('')
    setAmount('')
    setIsAdding(false)
  }

  const handleAdd = async () => {
    if (!selectedAccountId) {
      onValidationError('Select a connected account')
      return
    }
    const parsed = parseFloat(amount)
    if (isNaN(parsed) || parsed < 0 || parsed > 100) {
      onValidationError('Deposit charge must be a number between 0 and 100')
      return
    }
    await onAdd(selectedAccountId, parsed)
    resetForm()
  }

  return (
    <div className="flex flex-col gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
      <div className="flex flex-col gap-1">
        <Text className="text-sm font-medium text-gray-900 dark:text-white">Connected account overrides</Text>
        <Text className="text-xs text-gray-500 dark:text-gray-400">
          Deposit charges only apply to APA connected accounts. Deleting an override falls back to the organization amount
          for that account.
        </Text>
      </div>

      {overrides.length > 0 && (
        <div className="flex flex-col gap-2">
          {overrides.map((override) => (
            <div
              key={override.connectedAccountId}
              className="flex items-center justify-between gap-4 rounded-md border border-gray-200 px-3 py-2 dark:border-gray-700"
            >
              <div className="flex flex-col">
                <Text className="text-sm text-gray-900 dark:text-white">
                  {accountName(override.connectedAccountId)}
                </Text>
                <Text className="text-xs text-gray-500 dark:text-gray-400">{override.connectedAccountId}</Text>
              </div>
              <div className="flex items-center gap-2">
                <Text className="text-sm text-gray-900 dark:text-white">{override.feeAmount}%</Text>
                <Button
                  type="button"
                  plain
                  aria-label="Delete connected account override"
                  onClick={() => onDelete(override.connectedAccountId)}
                  disabled={isSubmitting}
                >
                  <Trash2 data-slot="icon" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isAdding ? (
        <div className="flex flex-col gap-3 rounded-md border border-gray-200 p-3 md:flex-row md:items-end dark:border-gray-700">
          <div className="w-full flex-1">
            <Field>
              <Label className="text-sm/6 font-medium text-gray-700 dark:text-gray-300">Connected account</Label>
              <Select
                value={selectedAccountId}
                onChange={(event) => setSelectedAccountId(event.target.value)}
              >
                <option value="">Select a connected account</option>
                {availableAccounts.map((account) => (
                  <option
                    key={account.id}
                    value={account.id}
                  >
                    {account.name} ({account.vesselName})
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <div className="w-full md:w-40">
            <Field>
              <Label className="text-sm/6 font-medium text-gray-700 dark:text-gray-300">Deposit (%)</Label>
              <Input
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </Field>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleAdd}
              disabled={isSubmitting}
            >
              Add
            </Button>
            <Button
              type="button"
              plain
              onClick={resetForm}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button
            type="button"
            outline
            onClick={() => setIsAdding(true)}
            disabled={isSubmitting || availableAccounts.length === 0}
          >
            <Plus data-slot="icon" />
            Add connected account
          </Button>
        </div>
      )}
    </div>
  )
}
